<template>
  <div class="schellong-app">
    <audio id="bell-audio" preload="auto" style="display:none">
      <source src="bell.wav" type="audio/wav" />
      <!-- Optional: <source src="/bell.mp3" type="audio/mpeg" /> -->
    </audio>
    <div v-if="currentStep < steps.length" class="card-container">
      <div v-if="steps[currentStep].type === 'instruction'" class="card">
        <h1>Schellong-Test</h1>
        <p>{{ steps[currentStep].text }}</p>
        <button @click="handleInstructionContinue">{{ steps[currentStep].button || 'Weiter' }}</button>
      </div>
      <div v-else-if="steps[currentStep].type === 'timer'" class="card">
        <div class="timer-circle-wrapper">
          <svg class="timer-circle" viewBox="0 0 220 220">
            <circle class="timer-bg" cx="110" cy="110" r="88" />
            <circle
              class="timer-progress"
              cx="110" cy="110" r="88"
              :stroke-dasharray="552.92"
              :stroke-dashoffset="(1 - timer / steps[currentStep].duration) * 552.92"
              transform="rotate(-90 110 110)"
            />
            <text x="110" y="114" text-anchor="middle" alignment-baseline="middle" class="timer-text">{{ formattedTime }}</text>
          </svg>
        </div>
        <p v-if="timer > 0">{{ steps[currentStep].text }}</p>
        <button v-if="timer <= 0" @click="handleTimerContinue">Weiter</button>
      </div>
      <div v-else-if="steps[currentStep].type === 'input'" class="card">
        <h2>Messung {{ measurements.length + 1 }}</h2>
        <form @submit.prevent="submitMeasurement">
          <p>{{ steps[currentStep].text }}</p>
          <div class="input-group" v-for="field in steps[currentStep].fields" :key="field">
            <label :for="field">
              <template v-if="field === 'sis'">Systolisch (mmHg):</template>
              <template v-else-if="field === 'dys'">Diastolisch (mmHg):</template>
              <template v-else-if="field === 'hr'">Herzfrequenz (bpm):</template>
              <template v-else>{{ field }}</template>
            </label>

            <input :id="field" v-model="input[field]" type="number" min="0" required />
          </div>
          <button type="submit">{{ steps[currentStep].button || 'Weiter' }}</button>
        </form>
      </div>
    </div>
    <div v-else class="summary-scroll">
  <div class="card summary-card" :class="{ 'pdf-export': isExportingPDF }">
    <h2>Schellong-Test Zusammenfassung</h2>
    <button v-if="!isExportingPDF" @click="downloadPDF" style="margin-bottom: 16px;">PDF herunterladen</button>
    <div class="summary-chart-wrapper">
      <canvas id="resultsChart" style="width:100%;height:auto;min-height:240px;"></canvas>
    </div>
    <div class="graph-note">
      Der grüne Bereich der Grafik stellt die Stehphase des Schellong-Tests dar.<br><br>
    </div>
    <div class="summary-explanation" v-if="!isExportingPDF">
      <strong>So interpretieren Sie Ihre Ergebnisse:</strong>
      <p>
        Während des Schellong-Tests werden Ihr Blutdruck und Ihre Herzfrequenz im Liegen und nach dem Aufstehen gemessen. Normalerweise sinken systolischer und diastolischer Blutdruck beim Aufstehen nur leicht oder bleiben stabil, während die Herzfrequenz moderat ansteigt.
        <br><br>
        <b>Auffällige Befunde:</b> Während der Stehphase: Ein deutlicher Blutdruckabfall (insbesondere systolisch), ein starker Anstieg der Herzfrequenz oder Symptome wie Schwindel können auf eine orthostatische Hypotonie oder andere Kreislaufprobleme hinweisen.
        <br><br>
        <i>Besprechen Sie diese Ergebnisse mit Ihrem Arzt oder Ihrer Ärztin. Sie können die Resultate als PDF herunterladen.</i>
      </p>
    </div>
    <div class="summary-table-wrapper">
      <table>
        <thead>
          <tr>
            <th>Minuten</th>
            <th>Systolisch<br>(mmHg)</th>
            <th>Diastolisch<br>(mmHg)</th>
            <th>Herzfrequenz<br>(bpm)</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(m, i) in measurements" :key="i" :class="{ 'standing-row': getPhaseLabels()[i] === 'Standing' }">
            <td>{{ getMeasurementTimes()[i] }}</td>
            <td>{{ m.sis }}</td>
            <td>{{ m.dys }}</td>
            <td>{{ m.hr }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <button v-if="!isExportingPDF" @click="restart">Neu starten</button>
  </div>
</div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import Chart from 'chart.js/auto';
import annotationPlugin from 'chartjs-plugin-annotation';
Chart.register(annotationPlugin);
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const isExportingPDF = ref(false);

const downloadPDF = async () => {
  isExportingPDF.value = true;
  await nextTick();
  // Set chart to A4 width minus padding (555pt ~ 1950px at 72dpi)
  const chartWrapper = document.querySelector('.summary-chart-wrapper');
  const chartCanvas = document.getElementById('resultsChart');
  const originalWidth = chartCanvas.style.width;
  const originalHeight = chartCanvas.style.height;
  // Use 555pt (595pt - 2*20pt), convert to px (1pt = 1.333px)
  const a4ChartPx = Math.round((595 - 40) * 1.333); // ~740px
  chartCanvas.style.width = a4ChartPx + 'px';
  chartCanvas.style.height = '400px';
  chartWrapper.style.width = a4ChartPx + 'px';
  chartWrapper.style.padding = '0';
  // Redraw chart at new size
  await nextTick();
  if (window.chartInstance && window.chartInstance.resize) {
    window.chartInstance.resize();
  }
  // Wait for DOM
  await new Promise(resolve => setTimeout(resolve, 100));

  const element = document.querySelector('.summary-card');
  if (!element) {
    // Restore
    chartCanvas.style.width = originalWidth;
    chartCanvas.style.height = originalHeight;
    chartWrapper.style.width = '';
    chartWrapper.style.padding = '';
    isExportingPDF.value = false;
    return;
  }
  // PDF generation
  const a4Width = 595;
  const a4Height = 842;
  const pdfCanvas = await html2canvas(element, { scale: 2, backgroundColor: '#fff' });
  const imgData = pdfCanvas.toDataURL('image/jpeg', 0.95);
  // Fit to A4
  const contentWidth = pdfCanvas.width;
  const contentHeight = pdfCanvas.height;
  let pdfWidth = a4Width;
  let pdfHeight = (contentHeight * a4Width) / contentWidth;
  if (pdfHeight > a4Height) {
    pdfHeight = a4Height;
    pdfWidth = (contentWidth * a4Height) / contentHeight;
  }
  const x = (a4Width - pdfWidth) / 2;
  const y = (a4Height - pdfHeight) / 2;
  const pdf = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'portrait' });
  pdf.addImage(imgData, 'JPEG', x, y, pdfWidth, pdfHeight);
  // --- Web Share API for iOS/Android: Share PDF directly instead of blob: link ---
  let shared = false;
  try {
    const pdfBlob = pdf.output('blob');
    const pdfFile = new File([pdfBlob], 'schellong-ergebnisse.pdf', { type: 'application/pdf' });
    if (navigator.canShare && navigator.canShare({ files: [pdfFile] })) {
      await navigator.share({
        files: [pdfFile],
        title: 'Schellong-Test Ergebnisse',
        text: 'schellong-test.com - Ergebnisse'
      });
      shared = true;
    }
  } catch (e) {
    // Ignore and fallback
  }
  if (!shared) {
    pdf.save('schellong-summary.pdf');
  }
  // Restore chart size
  chartCanvas.style.width = originalWidth;
  chartCanvas.style.height = originalHeight;
  chartWrapper.style.width = '';
  chartWrapper.style.padding = '';
  if (window.chartInstance && window.chartInstance.resize) {
    window.chartInstance.resize();
  }
  isExportingPDF.value = false;
};


// --- DEBUG MODE ---
// Set to true to skip the test and show the results page with dummy data
const DEBUG_MODE = false;


// Fully declarative protocol: edit this array to change the test flow
const steps = [
  {
    type: 'instruction',
    text: 'Dies ist ein Online-Schellong-Test. Er hilft Ihnen, verschiedene Formen der orthostatischen Intoleranz zu erfassen. Alles, was Sie benötigen, ist ein Blutdruckmessgerät und ein Sofa oder Bett. Der Test dauert etwa 30 Minuten. Legen Sie sich hin und drücken Sie Start, wenn Sie bereit sind.',
    button: 'Starten',
  },
  {
    type: 'input',
    text: 'Bitte messen Sie Ihren Blutdruck und Ihre Herzfrequenz im Liegen.',
    fields: ['sis', 'dys', 'hr'],
    button: 'Weiter',
    
  },
  {
    type: 'timer',
    text: 'Entspannen Sie sich und warten Sie im Liegen.',
    duration: 90,
  },
  {
    type: 'input',
    text: 'Bitte messen Sie Ihren Blutdruck und Ihre Herzfrequenz im Liegen.',
    fields: ['sis', 'dys', 'hr'],
    button: 'Weiter',
    
  },
  {
    type: 'timer',
    text: 'Entspannen Sie sich und warten Sie im Liegen.',
    duration: 90,
  },
  {
    type: 'input',
    text: 'Bitte messen Sie Ihren Blutdruck und Ihre Herzfrequenz im Liegen.',
    fields: ['sis', 'dys', 'hr'],
    button: 'Weiter',
    
  },
  {
    type: 'timer',
    text: 'Entspannen Sie sich und warten Sie im Liegen.',
    duration: 90,
  },
  {
    type: 'input',
    text: 'Bitte messen Sie Ihren Blutdruck und Ihre Herzfrequenz im Liegen.',
    fields: ['sis', 'dys', 'hr'],
    button: 'Weiter',
    
  },
  {
    type: 'timer',
    text: 'Entspannen Sie sich und warten Sie im Liegen.',
    duration: 90,
  },
  {
    type: 'input',
    text: 'Bitte messen Sie Ihren Blutdruck und Ihre Herzfrequenz im Liegen.',
    fields: ['sis', 'dys', 'hr'],
    button: 'Weiter',
    
  },
  {
    type: 'timer',
    text: 'Entspannen Sie sich und warten Sie im Liegen.',
    duration: 90,
  },
  {
    type: 'input',
    text: 'Bitte messen Sie Ihren Blutdruck und Ihre Herzfrequenz im Liegen.',
    fields: ['sis', 'dys', 'hr'],
    button: 'Weiter',
    
  },
  {
    type: 'instruction',
    text: 'Now, stand up quickly and remain standing. When you stand, press Continue to proceed with the next measurement.',
    button: 'Weiter',
  },
  {
    type: 'input',
    text: 'Bitte messen Sie Ihren Blutdruck und Ihre Herzfrequenz sofort im Stehen.',
    fields: ['sis', 'dys', 'hr'],
    button: 'Weiter',
    
    
  },
  {
    type: 'timer',
    text: 'Bleiben Sie aufrecht stehen.',
    duration: 90,
  },
  {
    type: 'input',
    text: 'Bitte messen Sie Ihren Blutdruck und Ihre Herzfrequenz im Stehen.',
    fields: ['sis', 'dys', 'hr'],
    button: 'Weiter',
    
  },
    {
    type: 'timer',
    text: 'Warten Sie im Stehen.',
    duration: 90,
  },
  {
    type: 'input',
    text: 'Bitte messen Sie Ihren Blutdruck und Ihre Herzfrequenz im Stehen.',
    fields: ['sis', 'dys', 'hr'],
    button: 'Weiter',
    
  },
    {
    type: 'timer',
    text: 'Warten Sie im Stehen.',
    duration: 90,
  },
  {
    type: 'input',
    text: 'Bitte messen Sie Ihren Blutdruck und Ihre Herzfrequenz im Stehen.',
    fields: ['sis', 'dys', 'hr'],
    button: 'Weiter',
    
  },
    {
    type: 'timer',
    text: 'Warten Sie im Stehen.',
    duration: 90,
  },
  {
    type: 'input',
    text: 'Bitte messen Sie Ihren Blutdruck und Ihre Herzfrequenz im Stehen.',
    fields: ['sis', 'dys', 'hr'],
    button: 'Weiter',
    
  },
    {
    type: 'timer',
    text: 'Warten Sie im Stehen.',
    duration: 90,
  },
  {
    type: 'input',
    text: 'Bitte messen Sie Ihren Blutdruck und Ihre Herzfrequenz im Stehen.',
    fields: ['sis', 'dys', 'hr'],
    button: 'Weiter',
    
  },
  {
    type: 'instruction',
    text: 'Now, lie down again, press Continue to proceed with the next measurement.',
    button: 'Weiter',
  },
  {
    type: 'input',
    text: 'Bitte messen Sie Ihren Blutdruck und Ihre Herzfrequenz im Liegen.',
    fields: ['sis', 'dys', 'hr'],
    button: 'Weiter',
    
  },
  {
    type: 'timer',
    text: 'Warten Sie im Liegen.',
    duration: 90,
  },
  {
    type: 'input',
    text: 'Bitte messen Sie Ihren Blutdruck und Ihre Herzfrequenz im Liegen.',
    fields: ['sis', 'dys', 'hr'],
    button: 'Weiter',
    
  },
  {
    type: 'timer',
    text: 'Warten Sie im Liegen.',
    duration: 90,
  },
  {
    type: 'input',
    text: 'Bitte messen Sie Ihren Blutdruck und Ihre Herzfrequenz im Liegen.',
    fields: ['sis', 'dys', 'hr'],
    button: 'Weiter',
    
  },
  {
    type: 'timer',
    text: 'Warten Sie im Liegen.',
    duration: 90,
  },
  {
    type: 'input',
    text: 'Bitte messen Sie Ihren Blutdruck und Ihre Herzfrequenz im Liegen.',
    fields: ['sis', 'dys', 'hr'],
    button: 'Fertig',
    
  }
];

const currentStep = ref(DEBUG_MODE ? steps.length : 0);
const measurements = ref(DEBUG_MODE ? [
  { sis: 120, dys: 80, hr: 70 },
  { sis: 115, dys: 78, hr: 69 },
  { sis: 112, dys: 77, hr: 66 },
  { sis: 110, dys: 75, hr: 65 },
  { sis: 115, dys: 78, hr: 65 },
  { sis: 120, dys: 78, hr: 66 },
  { sis: 125, dys: 80, hr: 75 },
  { sis: 130, dys: 85, hr: 98 },
  { sis: 115, dys: 78, hr: 112 },
  { sis: 120, dys: 78, hr: 126 },
  { sis: 125, dys: 80, hr: 168 },
  { sis: 130, dys: 85, hr: 74 },
  { sis: 115, dys: 78, hr: 65 },
  { sis: 130, dys: 85, hr: 74 },
  { sis: 115, dys: 78, hr: 65 },
  { sis: 120, dys: 78, hr: 66 }
] : []);
const input = ref({ sis: '', dys: '', hr: '' });
const timer = ref(0);
let timerInterval = null;

const formattedTime = computed(() => {
  const min = Math.floor(timer.value / 60).toString();
  const sec = (timer.value % 60).toString().padStart(2, '0');
  return `${min}:${sec}`;
});

watch(currentStep, (newStep) => {
  const step = steps[newStep];
  if (step && step.type === 'timer') {
    startTimer(step.duration);
  } else {
    clearInterval(timerInterval);
  }
});

function nextStep() {
  currentStep.value++;
  const step = steps[currentStep.value];
  if (step && step.type === 'input' && step.defaultValues) {
    input.value = { ...step.defaultValues };
  } else {
    input.value = { sis: '', dys: '', hr: '' };
  }
  if (currentStep.value >= steps.length) {
    clearInterval(timerInterval);
  }
}

function restart() {
  currentStep.value = 0;
  measurements.value = [];
  input.value = { sis: '', dys: '', hr: '' };
  timer.value = 0;
  clearInterval(timerInterval);
}

function startTest() {
  currentStep.value = 1;
  measurements.value = [];
  input.value = { sis: '', dys: '', hr: '' };
  timer.value = 0;
  clearInterval(timerInterval);
}

// --- Chart Logic ---
let chartInstance = null;

function getPhaseLabels() {
  // Return an array of phase labels ("Lying"/"Standing") for each measurement
  // Heuristic: if the input step text contains "standing" (EN) or "Stehen" (DE), mark as Standing, else Lying
  return steps.filter(s => s.type === 'input').map(s =>
    (/standing/i.test(s.text) || /stehen/i.test(s.text)) ? 'Standing' : 'Lying'
  );
}

function getMeasurementTimes() {
  // First measurement at 0, subsequent ones after 0.5 min delay before input
  let time = 0;
  const times = [];
  let first = true;
  for (const step of steps) {
    if (step.type === 'input') {
      if (first) {
        times.push(time); // first measurement at t=0
        first = false;
      } else {
        time += 0.5; // 30s before subsequent measurements
        times.push(time);
      }
    }
    if (step.type === 'timer') {
      time += (step.duration || 0) / 60;
    }
  }
  return times;
}

function getPhaseTransitions() {
  // Returns [{ time: <minutes>, type: 'Stand Up'|'Lie Down' }],
  // with the marker at the exact time the instruction step is shown
  let time = 0;
  const transitions = [];
  let first = true;
  for (const step of steps) {
    if (step.type === 'instruction') {
      // Marker at the exact current time
      if (/stand up/i.test(step.text)) {
        transitions.push({ time, type: 'Stand Up' });
      } else if (/lie down/i.test(step.text)) {
        transitions.push({ time, type: 'Lie Down' });
      }
    }
    if (step.type === 'input') {
      if (first) {
        first = false;
      } else {
        time += 0.5; // 30s before subsequent measurements
      }
    }
    if (step.type === 'timer') {
      time += (step.duration || 0) / 60;
    }
  }
  return transitions;
}

async function renderChart() {
  await nextTick();
  const canvas = document.getElementById('resultsChart');
  if (!canvas || !measurements.value.length) return;
  const ctx = canvas.getContext('2d');
  if (chartInstance) chartInstance.destroy();

  const labels = getMeasurementTimes();
  const phases = getPhaseLabels();
  const sis = measurements.value.map(m => Number(m.sis));
  const dys = measurements.value.map(m => Number(m.dys));
  const hr = measurements.value.map(m => Number(m.hr));

  // Phase transitions for vertical lines
  const transitions = getPhaseTransitions();

  // --- Standing phase annotation ---
  let standingStartIdx = phases.findIndex(p => p === 'Standing');
  let standingEndIdx = phases.length - 1 - [...phases].reverse().findIndex(p => p === 'Standing');
  let standingBox = null;
  if (standingStartIdx !== -1 && standingEndIdx !== -1 && standingEndIdx >= standingStartIdx) {
    const xMin = labels[standingStartIdx];
    const xMax = labels[standingEndIdx] ?? labels[labels.length - 1];
    standingBox = {
      type: 'box',
      xMin,
      xMax,
      backgroundColor: 'rgba(76, 220, 140, 0.13)', // light green
      borderWidth: 0,
      yScaleID: 'y',
    };
  }

  chartInstance = new Chart(ctx, {
    type: 'line',
    data: {
      // Use scatter datasets with x/y pairs for correct time placement
      datasets: [
        {
          label: 'Systolisch (mmHg)',
          data: labels.map((x, i) => ({ x, y: sis[i] })),
          borderColor: '#388e6c',
          backgroundColor: 'rgba(56,142,108,0.1)',
          tension: 0.2,
          showLine: true,
          fill: false
        },
        {
          label: 'Diastolisch (mmHg)',
          data: labels.map((x, i) => ({ x, y: dys[i] })),
          borderColor: '#56ab2f',
          backgroundColor: 'rgba(86,171,47,0.1)',
          tension: 0.2,
          showLine: true,
          fill: false
        },
        {
          label: 'Herzfrequenz (bpm)',
          data: labels.map((x, i) => ({ x, y: hr[i] })),
          borderColor: '#e67e22',
          backgroundColor: 'rgba(230,126,34,0.1)',
          tension: 0.2,
          showLine: true,
          fill: false
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'top' },
        title: { display: true, text: 'Messergebnisse im Zeitverlauf' },
        annotation: {
          annotations: {
            // Only the green background for standing phase
            ...(standingBox ? { standingPhase: standingBox } : {}),
          },
        },
        tooltip: {
          callbacks: {
            title: function(tooltipItems) {
              // tooltipItems[0].parsed.x is the time in minutes (float)
              const totalSeconds = Math.round(tooltipItems[0].parsed.x * 60);
              const min = Math.floor(totalSeconds / 60);
              const sec = totalSeconds % 60;
              let label = '';
              if (min > 0) label += `${min} minute${min !== 1 ? 's' : ''}`;
              if (min > 0 && sec > 0) label += ' ';
              if (sec > 0) label += `${sec} second${sec !== 1 ? 's' : ''}`;
              if (!label) label = '0 seconds';
              return label;
            }
          }
        }
      },
      scales: {
        x: {
          // title: { display: false },
          type: 'linear',
          min: 0,
          max: labels[labels.length - 1], // end at last measurement
          ticks: {
            color: '#2a6d4e',
            maxRotation: 60, // Tilt labels for space
            minRotation: 60, // Always tilt labels at 60 degrees
            autoSkip: false,
            // Show only 2-minute marks
            stepSize: 2,
            callback: function(value) {
              return `${value} min`;
            }
          }
        },
        y: {
          // title: { display: false },
          ticks: { color: '#2a6d4e' }
        }
      }
    }
  });

  if (chartInstance.$context && chartInstance.$context.chart && chartInstance.$context.chart.update) {
    chartInstance.$context.chart.update();
  } else {
    chartInstance.update();
  }
}

watch(measurements, async (newVal) => {
  if (currentStep.value >= steps.length) {
    await nextTick();
    renderChart();
  }
});

watch(currentStep, async (step) => {
  if (step >= steps.length) {
    await nextTick();
    renderChart();
  }
});

onMounted(() => {
  // Unlock bell.wav on first user interaction
  const unlockAudio = () => {
    const bell = document.getElementById('bell-audio');
    if (bell) {
      bell.play().then(() => bell.pause()).catch((e) => {
        console.log('Bell audio unlock failed:', e);
      });
    }
    document.removeEventListener('pointerdown', unlockAudio);
  };
  document.addEventListener('pointerdown', unlockAudio, { once: true });

  if (currentStep.value >= steps.length) {
    renderChart();
  }
});

let vibrationTimeout = null;

function startTimer(duration) {
  timer.value = duration;
  clearInterval(timerInterval);
  if (vibrationTimeout) {
    clearTimeout(vibrationTimeout);
    vibrationTimeout = null;
  }
  timerInterval = setInterval(() => {
    timer.value--;
    if (timer.value <= 0) {
      clearInterval(timerInterval);
      // Play bell sound
      const bell = document.getElementById('bell-audio');
      if (bell) {
        bell.currentTime = 0;
        bell.play();
      }
      // Do not vibrate here; vibrate is scheduled on button click
    }
  }, 1000);
}

function scheduleVibration(duration) {
  if (navigator.vibrate) {
    if (vibrationTimeout) clearTimeout(vibrationTimeout);
    vibrationTimeout = setTimeout(() => {
      navigator.vibrate([250, 100, 250]);
      vibrationTimeout = null;
    }, duration * 1000);
  }
}

function handleTimerContinue() {
  if (vibrationTimeout) {
    clearTimeout(vibrationTimeout);
    vibrationTimeout = null;
  }
  nextStep();
}

function handleInstructionContinue() {
  // If the next step is a timer, schedule vibration
  if (steps[currentStep + 1] && steps[currentStep + 1].type === 'timer') {
    scheduleVibration(steps[currentStep + 1].duration);
  }
  nextStep();
}

function submitMeasurement() {
  measurements.value.push({
    sis: input.value.sis,
    dys: input.value.dys,
    hr: input.value.hr
  });
  nextStep();
}

</script>

<style scoped>
.schellong-app {
  min-height: 100vh;
  background: linear-gradient(135deg, #e6f4ea 0%, #dbead6 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Segoe UI', Arial, sans-serif;
}
.card-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  box-sizing: border-box;
  padding-left: 0;
  padding-right: 0;
}


.summary-scroll {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  width: 100vw;
  min-height: 100vh;
  overflow-y: auto;
  padding-top: 2vh;
  background: none;
}


.summary-scroll {
  align-items: flex-start;
  height: 100vh;
  overflow-y: auto;
  width: 100vw;
  padding-top: 2vh;
}

.summary-card {
  width: 95vw;
  max-width: 700px;
  min-width: 320px;
  overflow: visible;
  margin: 2rem auto;
  box-sizing: border-box;
}

.summary-chart-wrapper {
  width: 100%;
  max-width: 700px;
  margin: 0 auto 2rem auto;
  padding: 0;
  box-sizing: border-box;
}

.summary-table-wrapper {
  width: 100%;
  max-width: 700px;
  margin: 0 auto 1.5rem auto;
  padding: 0;
  box-sizing: border-box;
  overflow-x: auto;
}

@media (max-width: 800px) {
  .summary-card, .summary-chart-wrapper, .summary-table-wrapper {
    max-width: 100vw;
    min-width: 0;
    padding-left: 0;
    padding-right: 0;
  }
}



.summary-chart-wrapper {
  width: 100%;
  min-height: 240px;
  margin-bottom: 2rem;
}

.pdf-export.summary-card {
  max-width: 595pt;
  width: 595pt;
  min-width: 595pt;
  margin: 0 auto;
  overflow: hidden;
  box-sizing: border-box;
  background: #f8fff8;
}

.pdf-export .summary-chart-wrapper {
  width: 100%;
  max-width: 555pt;
  min-width: 555pt;
  margin: 0 auto 2rem auto;
  padding: 0 20pt;
  box-sizing: border-box;
  overflow: visible;
}

.pdf-export .summary-table-wrapper {
  width: max-content;
  min-width: 0;
  margin-left: auto;
  margin-right: auto;
  margin-top: 0;
  margin-bottom: 0;
  padding: 0;
  box-sizing: border-box;
  overflow: visible;
}

.pdf-export #resultsChart {
  width: 100% !important;
  min-width: 1px;
  max-width: 100%;
  height: 400px !important;
}




.summary-table-wrapper {
  width: 100%;
  overflow-x: auto;
  margin-bottom: 1.5rem;
}


.card {
  background: #f8fff8;
  border-radius: 20px;
  box-shadow: 0 4px 24px rgba(60, 120, 60, 0.12);
  padding: 1rem 1.5rem;
  max-width: 400px;
  width: 100%;
  text-align: center;
  box-sizing: border-box;
  margin-left: auto;
  margin-right: auto;
}

@media (max-width: 500px) {
  .card {
    margin-left: 4vw;
    margin-right: 4vw;
    max-width: none;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }
}


@media (max-width: 500px) {
  .card {
    padding-left: 8vw;
    padding-right: 8vw;
    max-width: 100vw;
  }
  .card-container {
    padding-left: 0;
    padding-right: 0;
  }
}

html, body, #app {
  height: 100%;
  margin: 0;
  padding: 0;
  overflow: hidden;
}

body {
  /* Remove min-height and overflow-y for no scroll */
}

.schellong-app {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
}

h1, h2 {
  color: #2a6d4e;
  margin-bottom: 1rem;
}
.timer-circle-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 2rem 0 1.5rem 0;
}

.timer-circle {
    width: 100%;
    height: 100%;
  max-width: 80vw;
  max-height: 60vw;
  display: block;
}


@media (max-width: 600px) {
  .timer-circle {
    width: 100%;
    height: 100%;
    max-width: 96vw;
    max-height: 96vw;
  }
}

.timer-bg {
  fill: none;
  stroke: #e0e0e0;
  stroke-width: 12;
}

.timer-progress {
  fill: none;
  stroke: #2ecc40;
  stroke-width: 12;
  stroke-linecap: round;
  transition: stroke-dashoffset 1s linear;
}

.timer-text {
  font-size: 3rem;
  font-weight: bold;
  fill: #226d4e;
  dominant-baseline: middle;
}


.timer-bg {
  fill: none;
  stroke: #e6f4ea;
  stroke-width: 12;
}

.timer-text {
  font-size: 2.2rem;
  font-family: 'Segoe UI', Arial, sans-serif;
  font-weight: bold;
  fill: #388e6c;
  dominant-baseline: middle;
}
@media (max-width: 600px) {

  .timer-text {
    font-size: 1.3rem;
  }
}

button {
  background: linear-gradient(90deg, #a8e063 0%, #56ab2f 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 1rem;
  transition: background 0.2s;
}
button:hover {
  background: linear-gradient(90deg, #56ab2f 0%, #a8e063 100%);
}
.input-group {
  margin-bottom: 1rem;
  text-align: left;
}
.input-group label {
  display: block;
  margin-bottom: 0.2rem;
  color: #388e6c;
}
.input-group input {
  width: calc( 100% - 1rem);
  padding: 0.5rem;
  border: 1px solid #b5d6b2;
  border-radius: 6px;
  font-size: 1rem;
}
table {
  width: 100%;
  border-collapse: collapse;
  margin: 1.5rem 0;
}
th, td {
  border: 1px solid #b5d6b2;
  padding: 0.5rem 0.8rem;
}
th {
  background: #e6f4ea;
  color: #2a6d4e;
}
td {
  background: #f8fff8;
  color: #388e6c;
}
::v-deep .standing-row td {
  background: #d4f8e5 !important;
}
</style>
